import { PayloadAction, createSlice } from "@reduxjs/toolkit";

import type { RootState } from "app/store";
import type { CallAttachmentFields, PracticeCall } from "services/dialstack";

// Attachment fields applied by a manual attach (POST /dialstack/calls/:id/attach/),
// keyed by backend call id. The paginated call lists accumulate rows in local
// `usePagination` state, so an RTK list-tag invalidation cannot refresh a row
// beyond page one and is unsafe anyway (SN-18212). Instead the attach mutation
// records the changed fields here and every list applies them to the rows it
// holds — only these five fields, never the detail payload.
type CallAttachmentsState = {
  byCallId: Record<number, CallAttachmentFields>;
};

const initialState: CallAttachmentsState = { byCallId: {} };

// The auth logout thunk's fulfilled action type; matched by string so this
// slice does not import the (import-heavy) auth slice.
const LOGOUT_FULFILLED = "auth/logout/fulfilled";

export const pickAttachmentFields = (
  call: Pick<PracticeCall, "patient" | "contact" | "matched_on"> & {
    caller_relationship?: string;
    attached_at?: string | null;
    attached_by?: PracticeCall["attached_by"];
  }
): CallAttachmentFields => ({
  patient: call.patient,
  contact: call.contact ?? null,
  matched_on: call.matched_on,
  caller_relationship: call.caller_relationship ?? "",
  attached_at: call.attached_at ?? null,
  attached_by: call.attached_by ?? null
});

export const callAttachmentsSlice = createSlice({
  name: "callAttachments",
  initialState,
  reducers: {
    attachmentApplied: (
      state,
      action: PayloadAction<{ callId: number; fields: CallAttachmentFields }>
    ) => {
      state.byCallId[action.payload.callId] = action.payload.fields;
    },
    attachmentsCleared: (state) => {
      state.byCallId = {};
    }
  },
  extraReducers: (builder) => {
    builder.addMatcher(
      (action) => action.type === LOGOUT_FULFILLED,
      (state) => {
        state.byCallId = {};
      }
    );
  }
});

export const { attachmentApplied, attachmentsCleared } =
  callAttachmentsSlice.actions;

export const selectAttachmentOverlay = (state: RootState) =>
  state.callAttachments.byCallId;

// Merge the overlay over rows on read, so pages fetched after an attach (which
// the server already returns attached) and rows updated in place agree.
export const withAttachmentOverlay = <T extends { id: number }>(
  rows: T[],
  overlay: Record<number, CallAttachmentFields>
): T[] =>
  rows.map((row) => (overlay[row.id] ? { ...row, ...overlay[row.id] } : row));

// Voicemails are keyed by their own id but attached through their call: the
// backend syncs `patient`/`contact`/`matched_on` onto every linked voicemail,
// so the overlay is applied by `voicemail.call`. The relationship stays on the
// call.
export const withVoicemailAttachmentOverlay = <
  T extends {
    call: number | null;
    patient: CallAttachmentFields["patient"];
    contact?: CallAttachmentFields["contact"];
    matched_on: CallAttachmentFields["matched_on"];
  }
>(
  rows: T[],
  overlay: Record<number, CallAttachmentFields>
): T[] =>
  rows.map((row) => {
    const fields = row.call !== null ? overlay[row.call] : undefined;
    if (!fields) return row;
    return {
      ...row,
      patient: fields.patient,
      contact: fields.contact,
      matched_on: fields.matched_on
    };
  });

export default callAttachmentsSlice.reducer;
